import { type ApiEndpoint } from "@/shared/api-endpoint";
import { kPitchPaletteLength } from "@/shared/modal.api";
import { isObject, isNumberArray, isStringArray } from "@/shared/utils/typeGuards";

export const kDriverSelectorRoute = "/driver-selector";

/**
 * How each stem's pitch palette is extracted before the driver is picked:
 * "topk" (the SpliceGen 3.0 and 4.0 palette), "sparse" (2.5's), or "dense".
 * The Modal app defaults to "topk" when none is sent.
 */
export const kDriverSelectorModes = ["topk", "sparse", "dense"] as const;
export type DriverSelectorMode = (typeof kDriverSelectorModes)[number];

const isDriverSelectorMode = (value: unknown): value is DriverSelectorMode =>
    typeof value === "string" && (kDriverSelectorModes as readonly string[]).includes(value);

export interface DriverSelectorRequestBody {
    fileKeys: string[];
    mode?: DriverSelectorMode;
}

export interface DriverSelectorSuccessResponse {
    ok: true;
    pitchPalette: number[];
    driverIdx: number;
}

export interface DriverSelectorErrorResponse {
    ok: false;
    error: string;
}

export type DriverSelectorResponse = DriverSelectorSuccessResponse | DriverSelectorErrorResponse;

export const isDriverSelectorRequestBody = (value: unknown): value is DriverSelectorRequestBody =>
    isObject(value) &&
    isStringArray(value.fileKeys) &&
    value.fileKeys.length > 0 &&
    (value.mode === undefined || isDriverSelectorMode(value.mode));

export const isDriverSelectorSuccessResponse = (
    value: unknown,
): value is DriverSelectorSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    isNumberArray(value.pitchPalette) &&
    value.pitchPalette.length === kPitchPaletteLength &&
    typeof value.driverIdx === "number";

export const isDriverSelectorErrorResponse = (
    value: unknown,
): value is DriverSelectorErrorResponse =>
    isObject(value) && value.ok === false && typeof value.error === "string";

export const kDriverSelectorEndpoint: ApiEndpoint<
    DriverSelectorRequestBody,
    DriverSelectorSuccessResponse
> = {
    route: kDriverSelectorRoute,
    label: "Driver Selector",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v2.5",
    description:
        "Selects the best driver among a set of audio loops and returns its pitch palette, snapped to a scale.",
    isRequestBody: isDriverSelectorRequestBody,
    isSuccessResponse: isDriverSelectorSuccessResponse,
};
